/**
 * @license
 * Copyright UX Stack. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at LICENSE.md
 */

import { Logger } from './logger';
import { AutoCollectModule } from './module/autocollect/autocollect';
import { Module } from './module/module';
import { TrackingModule } from './module/tracking/tracking';
import { VitalsModule } from './module/vitals/vitals';
import { UXStackInterface, UXStackOptions } from './sdk';
import { WebStorage } from './storage';
import { UUID } from './uuid';

const SESSION_KEY = '__uxssid__';

export class UXStack implements UXStackInterface {
  // tslint:disable-next-line:variable-name
  private _isReady: boolean;
  // tslint:disable-next-line:variable-name
  private _timestamp: bigint;
  // tslint:disable-next-line:variable-name
  private _appId: string;
  private options?: UXStackOptions;
  private defaultOptions: UXStackOptions = {};
  private storage: WebStorage;
  private listeners: Array<() => void> = [];
  private modules: { [key: string]: Module } = {};

  constructor() {
    this._isReady = false;
    this.storage = new WebStorage([window.sessionStorage]);
    this._timestamp = BigInt(Date.now());
    this.defaultOptions = {
      autocollect: true,
      vitals: true,
      modules: {
        track: {
          url: 'https://cognitive.uxstack.cloud',
        },
      },
    };
  }

  public get isReady(): boolean {
    return this._isReady;
  }

  public get timestamp(): bigint {
    return this._timestamp;
  }

  public get appId(): string {
    return this._appId;
  }

  public get sessionId(): string {
    const id = this.storage.getItem(SESSION_KEY);

    if (id !== null && id !== '' && id.length === 36) {
      return id;
    }

    return this.generateSessionId();
  }

  public load(module: Module): void {
    module.load(this, this.options);

    this.modules[module.name] = module;
  }

  public getModule(name: string): any {
    return this.modules[name];
  }

  public create(appId: string, options?: UXStackOptions): void {
    if (!UUID.isValid(appId)) {
      Logger.error('app id is not valid.');

      return;
    }

    this._appId = appId;

    options = options || {};

    this.options = Object.assign({}, this.defaultOptions, options);

    this.verifySessionId();

    this._isReady = true;

    this.load(new TrackingModule());

    if (this.options.autocollect) {
      this.load(new AutoCollectModule());
    }

    if (this.options.vitals) {
      this.load(new VitalsModule());
    }

    this.listeners.forEach((listener) => listener());
  }

  public ready(listener: () => void): void {
    this.listeners.push(listener);
  }

  private verifySessionId(): void {
    const id = this.sessionId;

    if (!UUID.isValid(id)) {
      Logger.error('session id is not valid.');
    }
  }

  private generateSessionId(): string {
    const id = UUID.v4();

    this.storage.setItem(SESSION_KEY, id);

    return id;
  }
}
